// MA-265 — practice tap-a-word means DECODE SUPPORT (never replay), through
// the ONE audio home (NarrationSource, ADR-056 D6). The library's StoryReader
// tap-a-word (MA-184/MA-281) stays a pure replay of the word — that's a
// read-aloud affordance, correct for finished prose. Practice text is where a
// kid is actively decoding, so the SAME gesture must instead sound the word
// out: segment-level support for a decodable token, a whole-word + flagged-
// irregular-part callout for a scheduled heart word.
//
// Reuses decodabilityEngine.js (MA-259, the ONE decodability home) to grade +
// segment the word, and webSpeechSource's PHONEME_SAY (MA-181, the ONE
// phoneme→spoken-sound table) to pronounce each taught grapheme as its SOUND,
// not its letter name ("kuh", never "cee") — a raw-letter TTS read would be a
// real phonics regression (a kid sounding out c-a-t needs "kuh-a-tuh").
//
// A "decodable"-verdict word's segments (per graphemeTaught in
// decodabilityEngine.js) are ALWAYS either a single taught letter, "ck", a
// doubled consonant, or (R6+, ADR-070 OQ-1) a taught digraph (sh/ch/th) —
// never a vowel-team/r-controlled/split-e grapheme (those stay untaught at
// every modeled phase, so they'd grade "undecodable" first).
//
// MA-667 slice c1 (2026-09-19, evidence: ASR runs 35466494564/35479541285) —
// a THIRD, alternate SHAPE for the decodable branch, selected via
// `opts.shape`: "v2" ends every MULTI-SEGMENT decodable sound-out on the
// blended whole word (`<seg1> ... <seg2> ... <segN>. <Word>.`) and stops
// sharing one respelling between /a/ and /o/ (see webSpeechSource.ts's
// PHONEME_SAY for the current split). HONEST LIMIT: whisper-1 (the only ASR
// instrument available here) cannot grade VOWEL QUALITY, so "aa" cannot be
// read as a letter name is a phonetic-spelling judgement, not a
// machine-verified fact — it is exactly what the founder listen at MA-667's
// close checks. Heart and undecodable branches are UNCHANGED in v2 — they
// already contain the whole word (heart word) or reduce to the bare word by
// construction (undecodable) — see this file's header discussion of those
// two branches above.
//
// MA-667 slice c2 (2026-09-20, narration runs 35482212601/35489052946) —
// flips `DECODE_UTTERANCE_RUNTIME_SHAPE` to "v2" so both runtime callers
// (PassageReader.tsx, ReadingSession.tsx — unchanged two-argument calls)
// actually hear it. Round-1 operator ruling (same date, after a builder STOP
// found 21 single-grapheme v2 strings like "ss. S." with no recorded clip):
// a single-grapheme decodable token ("s", "m", "ll") has nothing to BLEND —
// appending it as a "word" makes the voice say the LETTER NAME, the exact
// regression MA-667 exists to remove. So the "end on the blended word" tail
// is gated on segments.length >= 2; a one-segment decodable token's v2 is
// IDENTICAL to v1 (the bare sound, already recorded under v1 in every prior
// narration run — no new clip needed). `DECODE_V2_SAY_OVERRIDES` is gone:
// slice c2's step 4 measured that moving PHONEME_SAY["/a/"] to "aa" globally
// changes zero narratable strings once v1 sound-out text leaves the
// denominator (v1 is only ever requested by asrAudit's historical replay,
// which doesn't feed the narration denominator), so the split is now a
// single global table again (see webSpeechSource.ts).
export type DecodeUtteranceShape = "v1" | "v2";

/** The shape the RUNTIME (PassageReader.tsx:65, ReadingSession.tsx:1262)
 *  actually requests today — both call sites pass no third argument, so
 *  they always get this shape regardless of what buildDecodeUtterance's own
 *  default resolves to; this constant documents that fact, it doesn't
 *  independently control it. MA-667 slice c2 (narration runs 35482212601 /
 *  35489052946): flipped to "v2" now that the manifest carries a v2 clip for
 *  every string v2 can request (single-grapheme tokens keep their existing
 *  v1 clip — see the segments.length >= 2 gate below). */
export const DECODE_UTTERANCE_RUNTIME_SHAPE: DecodeUtteranceShape = "v2";

// The grapheme -> sound table below covers decodabilityEngine's full closed
// MULTI_GRAPHEMES set (imported, not duplicated), not just the taught-letter
// subset, so no grapheme segment() can emit falls back to a raw-letter TTS read.
// MA-1169: a "heart"-verdict word's TRICKY-PART callout no longer reads this
// table at all. It used to speak each untaught grapheme's REGULAR sound, which
// is wrong in exactly the words that are heart words ('have' -> "ay", 'one' ->
// "oh"); it now reads the curated per-word table in heartWordCallouts.ts.
import { MULTI_GRAPHEMES, gradeWord, segment } from "../curriculum/decodabilityEngine";
import { PHONEME_SAY } from "./webSpeechSource";
// MA-1169 — the curated heart-word table (what is tricky in each heart word and
// the sound those letters really make there).
import { HEART_WORD_IRREGULAR, heartWordCallout } from "./heartWordCallouts";
// MA-507 — the ONE word-normalization decode-support keys are built from,
// shared with the generation-side enumerator and the library tap-a-word's
// runtime lookup. See that file's header for the case-sensitivity defect
// this closes.
import { normalizeDecodeWord } from "./decodeWordNormalize";

// Grapheme -> PHONEME_SAY key. Doubled consonants + "ck" collapse to their
// single-letter sound (a doubled/ck grapheme is only ever taught when its
// base letter(s) are, and it's pronounced the same single sound). Silent-
// letter digraphs (wh/ph/kn/wr/mb/gn/tch/dge/que) reuse the existing single-
// consonant key they actually sound like, rather than minting a near-
// duplicate PHONEME_SAY entry. Vowel teams/r-controlled/split-e reuse the
// matching long-vowel key where the sound coincides (ai/ay/ei/ey/eigh -> /A/,
// ee/ea -> /E/, oa/igh/ie -> /I//O/ as appropriate) and otherwise use the new
// dedicated keys added to PHONEME_SAY for this slice (/ow/, /oo/, /oi/, /au/,
// /ar/, /or/, /er/, /air/, /ear/, /ure/, /qu/). This is a phonics-teaching
// APPROXIMATION (a grapheme like "ea"/"ough" has more than one real-world
// pronunciation) — same disclosed-approximation tier as the rest of this
// item, never a claim of full phonetic precision.
export const GRAPHEME_TO_PHONEME_KEY: Record<string, string> = {
  b: "/b/", c: "/k/", d: "/d/", f: "/f/", g: "/g/", h: "/h/", j: "/j/",
  k: "/k/", l: "/l/", m: "/m/", n: "/n/", p: "/p/", r: "/r/", s: "/s/",
  t: "/t/", v: "/v/", w: "/w/", y: "/y/", z: "/z/",
  a: "/a/", e: "/e/", i: "/i/", o: "/o/", u: "/u/",
  ck: "/k/",
  bb: "/b/", cc: "/k/", dd: "/d/", ff: "/f/", gg: "/g/", ll: "/l/",
  mm: "/m/", nn: "/n/", pp: "/p/", rr: "/r/", ss: "/s/", tt: "/t/",
  vv: "/v/", ww: "/w/", zz: "/z/", kk: "/k/",
  // consonant digraphs
  sh: "/sh/", ch: "/ch/", th: "/th/", ng: "/ng/",
  wh: "/w/", ph: "/f/", qu: "/qu/",
  kn: "/n/", wr: "/r/", mb: "/m/", gn: "/n/", // silent first/second letter
  tch: "/ch/", dge: "/j/", que: "/k/",
  // vowel teams
  ai: "/A/", ay: "/A/", ei: "/A/", ey: "/A/", eigh: "/A/",
  ee: "/E/", ea: "/E/",
  oa: "/O/", ough: "/O/",
  igh: "/I/", ie: "/I/",
  ow: "/ow/", ou: "/ow/",
  oi: "/oi/", oy: "/oi/",
  oo: "/oo/", ew: "/oo/", ue: "/oo/", ui: "/oo/",
  au: "/au/", aw: "/au/", augh: "/au/",
  // r-controlled
  ar: "/ar/", or: "/or/", er: "/er/", ir: "/er/", ur: "/er/",
  air: "/air/", ear: "/ear/", ure: "/ure/",
  // split-e (segment() emits these as "<vowel>_e")
  a_e: "/A/", e_e: "/E/", i_e: "/I/", o_e: "/O/", u_e: "/U/",
};

/** Every non-doubled/non-ck entry in decodabilityEngine's closed
 *  MULTI_GRAPHEMES list, plus the 5 split-e forms segment() can emit (the
 *  heart callout's old untaught-grapheme set before MA-1169; still the set
 *  every grapheme sound must cover). Exported for the completeness
 *  bite test (a future MULTI_GRAPHEMES addition with no matching sound here
 *  would otherwise silently regress to raw-letter TTS reading). */
export const CALLOUT_GRAPHEMES: string[] = [
  ...MULTI_GRAPHEMES.filter((g: string) => g !== "ck" && !(g.length === 2 && g[0] === g[1])),
  "a_e", "e_e", "i_e", "o_e", "u_e",
];

/** A grapheme's spoken SOUND (PHONEME_SAY's tuned pronunciation), falling
 *  back to the raw grapheme letters when no mapping exists (mirrors
 *  PHONEME_SAY's own "unknown symbols fall through" convention). MA-667
 *  slice c2 deleted the v2-only override table: PHONEME_SAY is now the one
 *  table both v1 and v2 read (see this file's header). */
function graphemeSound(g: string): string {
  // MA-765 — x is the cluster /k/+/s/. It is voiced as TWO house-convention
  // segments ("kuh ... ss", each read from PHONEME_SAY so it can never drift
  // from the k and s sounds); a vowel-less one-segment respelling ("ks"/"kss")
  // is read by the voice as letter names, and bare "x" as "ex". The join below
  // is the same " ... " separator every other segment uses.
  if (g === "x") return `${PHONEME_SAY["/k/"]} ... ${PHONEME_SAY["/s/"]}`;
  const key = GRAPHEME_TO_PHONEME_KEY[g];
  if (!key) return g;
  return PHONEME_SAY[key];
}

/** MA-765 — the `al` family (all/alk/alm: wall, walk, small, calm, ...) is NOT
 *  sounded out letter by letter: /a/ here is /aw/ ("aa" is wrong) and the l in
 *  alk/alm is silent (a "lll" is wrong). The phonics ladder teaches none of
 *  these units, so — like any unit the phase has not taught — the tap replays
 *  the whole word. Scoped to the AUDIO utterance only: decodabilityEngine's
 *  grading (which gates content serving) is deliberately untouched. */
export const AL_FAMILY = /a(?:ll|lk|lm|lt|ld)/;

/** MA-805 — long-vowel closed-syllable exceptions: `old`/`olt`, `ild`, `ind`
 *  and `ost` words whose vowel is LONG (cold /oʊ/, child /aɪ/, find /aɪ/, most
 *  /oʊ/). Sounding them out letter by letter reads a SHORT vowel ("kuh ... ah
 *  ... lll ... duh" for "cold"), which is the wrong word. The ladder teaches no
 *  such unit, so — like the al family — the tap replays the WHOLE word (operator
 *  ruling). Deliberately TIGHT, not a blanket regex, because the same letters are
 *  short in other words: cost/lost/frost/moss (short o), wind (noun, short i),
 *  build/gild/guild (short i), children (short i), hostile/hostage, window/index/
 *  Indian/Cindy/hinder, wilderness, mildew, kindergarten, kindle. Anchored whole-word patterns with a closed affix set:
 *   - o(?:ld|lt) anywhere NOT preceded by another o (bold, cold, golden, colt,
 *     bolt, molten, soldier ...; NOT oo+ld/lt: schoolteacher, cooldown).
 *     Arnold/Harold/scaffold/solder would also match (schwa/short) — none is in
 *     the corpus, so there is no pin for them;
 *   - ild: wild/mild(+er/est/ly/ness), child(+ish/hood/like) — NOT children;
 *   - ind: bind/blind/find/grind/hind/kind/mind/rind with be-/re-/un-/man-
 *     and s/ed/ing/ly/ness/less; "-er" only for bind/find/grind/mind (finder,
 *     reminder; NOT hinder); NOT wind, windy, window, index, cinder, Mindy;
 *   - MA-806 oll/olk (long o, silent l in olk): roll, stroll, scroll, troll, toll,
 *     poll, droll, knoll (+s/ed/ing/er, un-/en-/pay-/egg-/bed-), folk/yolk (+s,
 *     folklore) — NOT doll, dolly, collect, pollen, lollipop, mollusc, molly,
 *     holly, jolly, folly, follow, hollow, trolley, pollute (short o);
 *   - ost: m/p/h/gh + ost (most, post, host, ghost, postman, ghostly) — NOT
 *     cost/lost/frost/hostile. Scoped to the AUDIO utterance only, like AL_FAMILY. */
export const LONG_VOWEL_WHOLE_WORD = new RegExp(
  [
    "(?<!o)o(?:ld|lt)",
    "^(?:wild|mild)(?:er|est|ly|ness)?$",
    "^child(?:ish|hood|like)?$",
    "^(?:be|re|un|man)?(?:b|bl|f|gr|h|k|m|r)ind(?:s|ed|ing|ly|ness|less)?$",
    "^(?:re)?(?:b|f|gr|m)inders?$",
    "^(?:un|en|pay|egg|bed)?(?:s?cr|str|dr|tr|kn|p|t|r)oll(?:s|ed|ing|er|ers)?$",
    "^(?:f|y)olk(?:s|lore)?$",
    "^(?:m|p|h|gh)ost(?:s|ed|ing|er|ers|ly|man|men|card|ess)?$",
  ].join("|"),
);

/** MA-826 — w+a words whose a is NOT the short /æ/ the ladder teaches: after w
 *  the vowel is /ɒ/ or /ɔ/ (wash, wand, want, swan, swat, wasp), so the sound-out
 *  "wuh ... aa ... shh" contradicts the blended word. The ladder teaches no w+a
 *  unit, so — like AL_FAMILY and LONG_VOWEL_WHOLE_WORD — the tap replays the
 *  WHOLE word (operator ruling). Deliberately TIGHT (anchored stems + a closed
 *  affix set), because w+a is short in other words: wag, wags, wagged, wax,
 *  waxed, swam, wagon, wagons, swagger (pinned non-matches). Stems: wash, wand,
 *  want, swan, swat, wasp, plus swap/swamp/wad (same /ɒ/; none in the corpus
 *  today, listed so a future story does not regress). Scoped to the AUDIO
 *  utterance only, like AL_FAMILY. */
export const WA_NOT_SHORT_WHOLE_WORD = new RegExp(
  [
    "^wash(?:es|ed|ing|er|ers)?$",
    "^wands?$",
    "^want(?:s|ed|ing)?$",
    "^swans?$",
    "^swat(?:s|ted|ting|ter|ters)?$",
    "^wasps?$",
    "^swap(?:s|ped|ping)?$",
    "^swamps?$",
    "^wads?$",
  ].join("|"),
);

/** MA-826 — a multi-letter word with NO vowel letter (a e i o u y) has no
 *  syllable to blend: it is an initialism or abbreviation ("tv" — said as the
 *  letter names "tee vee", "dr", "mr", "pm", "st") or an interjection spelled
 *  without a vowel ("hmm", "grr", "brr", "shh"). Its sound-out ("tuh ... vvv.
 *  Tv.") is never the word, so it replays WHOLE (operator ruling: `tv`). Applied
 *  only when segment() yields 2 or more graphemes, so a one-grapheme token
 *  ("s", "m", "ll") keeps its bare sound (MA-667 round-1 ruling). */
export const NO_VOWEL_WHOLE_WORD = /^[^aeiouy]+$/;

/** MA-801 — x words whose x is NOT a plain /ks/ and so are replayed WHOLE
 *  rather than sounded out:
 *   - a bare "x" token: the letter name is the honest reading (its only corpus
 *     use is a multiplication sign, "1 x 1 = 1"); replaying the whole word "x"
 *     is the operator ruling, and it undoes MA-765's lone-x "kuh ... ss";
 *   - an initial x ("Xun", "xylophone": /z/ or a name — rare and irregular).
 *  (A "...xs" rule briefly covered the library's "foxs", which was "fox's" with
 *  its apostrophe stripped; MA-804 keeps the apostrophe and replays "fox's"
 *  whole, so nothing in the corpus reaches such a rule and it was removed.) */
export const X_WHOLE_WORD = /^x/;

/** MA-801 — word-initial "ex" + vowel (exact, exam, exist, exotic, exit, ...)
 *  is /gz/ (operator ruling: treat every ex+vowel word-initial as /gz/, spoken
 *  from the house /g/ and /z/ sounds exactly as MA-765 builds /ks/ from /k/ +
 *  /s/). "ex" + consonant (expect, extra, next) stays /ks/; ex+h (exhaust,
 *  exhibit) is also /gz/ but is NOT covered here (follow-up). */
export const X_GZ_INITIAL = /^ex[aeiou]/;

/** MA-681 — the SEGMENT portion of the sound-out utterance, built from an
 *  authored grapheme list (a sound-box record's `targetGraphemes`) instead of
 *  re-segmenting a word. Byte-identical to buildDecodeUtterance's "v1" shape
 *  for a decodable word ("kuh ... aa ... tuh", never ending on the word), and
 *  deliberately WITHOUT v2's blended-word tail: the sound-box cue is the
 *  segmented stimulus of ms.read.04 (sounds to letters; MA-1065) — saying the
 *  whole word would hand the child the answer. See ADR-055's MA-681 addendum. */
export function buildSegmentedSoundOut(graphemes: readonly string[]): string {
  return graphemes.map((g) => graphemeSound(g)).join(" ... ");
}

/** Builds the spoken decode-support utterance for `word` at `phaseId`:
 *  - decodable: segment-level sound-out. `opts.shape` picks the SHAPE,
 *    defaulting to DECODE_UTTERANCE_RUNTIME_SHAPE ("v2" as of MA-667 slice
 *    c2). "v1" — pure segments, no word ("kuh ... ah ... tuh" for "cat";
 *    never ends on the word). "v2" — when there are >= 2 segments, the same
 *    segments PLUS the blended whole word appended ("kuh ... aa ... tuh.
 *    Cat." for "cat"; first letter upper-cased, per MA-667's acceptance:
 *    "the sound-out utterance for a decodable word ends with the blended
 *    whole word so a tap always ends on the word itself"); with exactly 1
 *    segment (a bare single-grapheme token like "s"/"m"/"ll"), v2 === v1 —
 *    see the segments.length >= 2 gate below (round-1 operator ruling,
 *    2026-09-20).
 *  - heart (scheduled sight word): the whole word + its irregular part(s):
 *    the letters by name and the sound they really make in this word, from
 *    the curated table ("said. The tricky part: A-I says eh.", MA-1169) —
 *    UNCHANGED in v2;
 *    it already contains the whole word at the start (see this file's
 *    header discussion of MA-667's scope).
 *  - undecodable (not yet scheduled / irregular beyond this phase): plain
 *    whole-word replay — no decode claim beyond what's actually taught —
 *    UNCHANGED in v2 (it's already just the bare word).
 *  Pure string in/out — no audio I/O; the caller passes the result to
 *  NarrationSource.play(). */
export function buildDecodeUtterance(
  word: string,
  phaseId: string,
  opts?: { shape?: DecodeUtteranceShape },
): string {
  const shape: DecodeUtteranceShape = opts?.shape ?? DECODE_UTTERANCE_RUNTIME_SHAPE;
  const norm = normalizeDecodeWord(word);
  if (!norm) return word;
  // MA-804 — a word with an INTERNAL apostrophe (a contraction or possessive:
  // "he'll", "don't", "fox's") is NEVER sounded out, at any phase: segment()
  // has no unit for the apostrophe, and the letters-only sound-out it used to
  // get was for a different word ("hell", "dont", "foxs"). Replay it whole,
  // apostrophe kept, so the recorded/TTS string says the word as written.
  if (norm.includes("'")) return norm;
  const { verdict } = gradeWord(norm, phaseId);
  if (verdict === "decodable") {
    if (AL_FAMILY.test(norm)) return norm; // MA-765 — untaught unit -> whole word
    if (LONG_VOWEL_WHOLE_WORD.test(norm)) return norm; // MA-805 — long-vowel old/ild/ind/ost/olt -> whole word
    if (X_WHOLE_WORD.test(norm)) return norm; // MA-801 — irregular/lone x -> whole word
    if (WA_NOT_SHORT_WHOLE_WORD.test(norm)) return norm; // MA-826 — w+a not short -> whole word
    if (NO_VOWEL_WHOLE_WORD.test(norm) && segment(norm).length >= 2) return norm; // MA-826 — no vowel (tv, hmm) -> whole word
    const gz = X_GZ_INITIAL.test(norm);
    const segments = segment(norm).map((g: string, i: number) =>
      // MA-801: the x of an initial ex+vowel word is /gz/ ("guh ... zz").
      gz && i === 1 && g === "x" ? `${PHONEME_SAY["/g/"]} ... ${PHONEME_SAY["/z/"]}` : graphemeSound(g),
    );
    // MA-667 round-1 ruling: the blended-word tail only makes sense when
    // there's more than one segment to blend. A single-grapheme decodable
    // token ("s", "m", "ll") has nothing to blend, and appending it as a
    // "word" would make the voice say the LETTER NAME ("ss. S.") — the exact
    // defect MA-667 exists to remove. So v2 === v1 (bare sound only) for a
    // one-segment decodable token.
    // MA-765: x is ONE grapheme (one array element) voiced as two spoken
    // segments, so a lone "x" token still counts as one segment here and gets no
    // blended tail (a tail would be the letter name "X").
    if (shape === "v2" && segments.length >= 2) {
      const blended = norm.charAt(0).toUpperCase() + norm.slice(1);
      return `${segments.join(" ... ")}. ${blended}.`;
    }
    return segments.join(" ... ");
  }
  if (verdict === "heart") {
    // MA-1169 — the callout names the letters that are tricky IN THIS WORD and
    // the sound they really make here, from the one curated table (see
    // heartWordCallouts.ts). It no longer guesses from the phase's untaught
    // graphemes and their REGULAR sounds ('have' said "ay", 'one' said "oh").
    // A heart word with no entry is a test failure (ma1169HeartWordCallouts);
    // at runtime it replays the plain word, never a wrong sound.
    const entry = Object.prototype.hasOwnProperty.call(HEART_WORD_IRREGULAR, norm) ? HEART_WORD_IRREGULAR[norm] : undefined;
    return entry ? heartWordCallout(norm, entry) : norm;
  }
  // MA-507 — `norm`, not the caller's raw-case `word`: the generation-side
  // enumerator (scripts/content/narratableStrings.mjs) always calls this
  // with an already-lowercased word, so its "undecodable -> plain word"
  // branch produces a lowercase corpus entry. A caller here (e.g.
  // PassageReader/ReadingSession's practice tap-a-word, which passes the
  // tapped token's RAW case) must return the identical lowercase form or a
  // sentence-initial capitalized word's decode support silently misses the
  // manifest and falls back to WebSpeech — the same defect this item fixes
  // for the library's pure-replay tap (see decodeWordNormalize.ts).
  return norm;
}
